import c from 'classnames';
import { Button, Card, TextLink, Typography } from 'components';
import { CardErrorBoundaryWithSpinfinity } from 'components/Card/CardErrorBoundaryWithSpinfinity';
import { FeedbackWidget } from 'components/FeedbackWidget/FeedbackWidget';
import { Markdown } from 'components/Markdown/Markdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { FeedbackPageType } from 'types/Feedback';

import useCurrentKitAndTinyAccount from 'hooks/useCurrentKitAndTinyAccount';
import usePersonalizedNote from 'services/general/personalizedNote';

import { MainFindings } from './MainFindings';
import styles from './NoteCard.module.scss';

const LEGACY_MARKDOWN_CARD_PATTERN = /<MarkdownCard/; // Legacy component
const LEGACY_MAIN_FINDING_PATTERN = /(\*\*)?Main Finding(\*\*)?/; // If Mainfindings are 0 we still show a short text. This regex cover those cases.

function isLegacyNote(note: string | undefined): boolean {
  const noteText = note ?? '';
  return (
    LEGACY_MARKDOWN_CARD_PATTERN.test(noteText) ||
    LEGACY_MAIN_FINDING_PATTERN.test(noteText)
  );
}

const NoteCardContent = () => {
  const { currentKitId, currentTinyAccount } = useCurrentKitAndTinyAccount();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data } = usePersonalizedNote();

  const shouldShowNewMainFindings = !isLegacyNote(data?.original_value);

  // markdown from DB
  const personalizedNoteMarkdown = data?.original_value;
  if (!currentKitId) return <></>;

  return (
    <Card title={`About ${currentTinyAccount?.first_name}'s results`}>
      <div
        className={c(
          styles.cardContent,
          !personalizedNoteMarkdown && styles.noContent,
        )}
      >
        {personalizedNoteMarkdown ? (
          <Markdown
            customOverride={{
              a: ({ href, children }: { href: string; children: string }) => {
                return (
                  <TextLink
                    label={children}
                    to={href}
                    className={styles.link}
                  />
                );
              },
            }}
          >
            {personalizedNoteMarkdown}
          </Markdown>
        ) : (
          <Typography variant='body-s' serif>
            {`Our microbiome experts are preparing a review of your results that will highlight our main recommendations based on your results and survey answers. We will send you another email when your personal note is ready.`}
            <br />
            <br />
            {`Please do not wait for this to start reviewing your results and action plan - this is just an extra bonus to help you interpret your report.`}
          </Typography>
        )}

        {shouldShowNewMainFindings ? <MainFindings /> : null}
        <Button
          color='grey'
          className={styles.button}
          width='full'
          onClick={() => {
            navigate(
              `${pathname}/insights/all-insights?evaluations=red%2Corange%2Cgreen%2Cgrey`,
            );
          }}
        >
          See all metrics
        </Button>
        <FeedbackWidget
          kitId={currentKitId}
          pageType={FeedbackPageType.REVIEW}
          pageId={0}
        />
      </div>
    </Card>
  );
};

export const NoteCard = () => {
  return (
    <CardErrorBoundaryWithSpinfinity cardName='NoteCard'>
      <NoteCardContent />
    </CardErrorBoundaryWithSpinfinity>
  );
};
